import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Message as MessageIcon,
  Print as PrintIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import { ro } from 'date-fns/locale';

interface Patient {
  id: string;
  name: string;
  species: string;
  breed: string;
  sex: string;
  weight?: number;
  photoUrl?: string;
  birthDate?: string;
  microchip?: string;
  sterilized?: boolean;
  allergies?: string[];
  aggressive?: boolean;
  warnings?: string[];
}

interface Owner {
  id: string;
  name: string;
  phone?: string;
}

interface Doctor {
  id: string;
  name: string;
}

interface Appointment {
  id: string;
  start: string;
  end: string;
  service: string;
  reason: string;
  status: 'pending' | 'confirmed' | 'checked_in' | 'in_consultation' | 'completed' | 'cancelled';
  createdAt: string;
  location?: string;
  patient: Patient;
  owner: Owner;
  doctor: Doctor;
  lastVisit?: {
    date: string;
    type: string;
  };
  nextDue?: string;
}

interface AppointmentDetailsModalProps {
  open: boolean;
  onClose: () => void;
  appointment: Appointment | null;
  onStatusChange: (appointmentId: string, newStatus: string) => Promise<void>;
  onReschedule: (appointmentId: string, newStart: string, newEnd: string, note?: string) => Promise<void>;
  onCancel: (appointmentId: string, notifyOwner: boolean, reason?: string) => Promise<void>;
  onAddNote: (appointmentId: string, note: string) => Promise<void>;
}

const AppointmentDetailsModal: React.FC<AppointmentDetailsModalProps> = ({
  open,
  onClose,
  appointment,
  onStatusChange,
  onReschedule,
  onCancel,
  onAddNote
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [quickNote, setQuickNote] = useState('');
  const [notifyOwner, setNotifyOwner] = useState(true);
  const [cancelReason, setCancelReason] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (appointment) {
      setQuickNote('');
      setNotifyOwner(true);
      setCancelReason('');
      setShowReschedule(false);
      setShowCancel(false);
      setShowNote(false);
    }
  }, [appointment]);

  if (!appointment) return null;

  const formatDate = (dateString: string) => {
    try {
      const date = parseISO(dateString);
      if (!isValid(date)) return 'Data indisponibilă';
      return format(date, 'EEEE, d MMM yyyy, HH:mm', { locale: ro });
    } catch {
      return 'Data indisponibilă';
    }
  };

  const formatDateShort = (dateString: string) => {
    try {
      const date = parseISO(dateString);
      if (!isValid(date)) return 'Data indisponibilă';
      return format(date, 'dd MMM yyyy, HH:mm', { locale: ro });
    } catch {
      return 'Data indisponibilă';
    }
  };

  const formatTimeRange = (start: string, end: string) => {
    try {
      const startDate = parseISO(start);
      const endDate = parseISO(end);
      if (!isValid(startDate) || !isValid(endDate)) return 'Interval indisponibil';
      
      const startTime = format(startDate, 'HH:mm');
      const endTime = format(endDate, 'HH:mm');
      return `${startTime}–${endTime}`;
    } catch {
      return 'Interval indisponibil';
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'pending':
        return { label: 'În așteptare', color: 'warning' as const, icon: <AccessTimeIcon /> };
      case 'confirmed':
        return { label: 'Confirmată', color: 'success' as const, icon: <CheckCircleIcon /> };
      case 'checked_in':
        return { label: 'Check-in', color: 'info' as const, icon: <CheckCircleIcon /> };
      case 'in_consultation':
        return { label: 'În consultație', color: 'primary' as const, icon: <ScheduleIcon /> };
      case 'completed':
        return { label: 'Finalizată', color: 'success' as const, icon: <CheckCircleIcon /> };
      case 'cancelled':
        return { label: 'Anulată', color: 'error' as const, icon: <CancelIcon /> };
      default:
        return { label: 'Necunoscut', color: 'default' as const, icon: <AccessTimeIcon /> };
    }
  };

  const statusInfo = getStatusInfo(appointment.status);

  // Logic for button visibility and state
  const canCheckIn = ['pending', 'confirmed'].includes(appointment.status);
  const canOpenConsult = ['checked_in', 'confirmed'].includes(appointment.status);
  const canReschedule = !['cancelled', 'completed'].includes(appointment.status);
  const canCancel = !['cancelled', 'completed'].includes(appointment.status);
  const canEdit = !['cancelled', 'completed'].includes(appointment.status);
  const isInConsultation = appointment.status === 'in_consultation';
  const isPastDue = new Date(appointment.start) < new Date() && !['completed', 'cancelled'].includes(appointment.status);

  const handleCheckIn = async () => {
    if (!canCheckIn) return;
    
    setLoading(true);
    try {
      await onStatusChange(appointment.id, 'checked_in');
    } catch (error) {
      console.error('Eroare la check-in:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenConsultation = () => {
    if (!canOpenConsult) return;
    
    // If pending, auto-check-in first
    if (appointment.status === 'pending') {
      handleCheckIn().then(() => {
        navigate(`/visit/${appointment.id}`);
        onClose();
      });
    } else {
      navigate(`/visit/${appointment.id}`);
      onClose();
    }
  };

  const handleReschedule = async () => {
    if (!canReschedule) return;
    setShowReschedule(true);
  };

  const handleCancel = async () => {
    if (!canCancel) return;
    setShowCancel(true);
  };

  const handlePrint = () => {
    // TODO: Implement print functionality
    window.open(`/api/appointments/${appointment.id}/print`, '_blank');
  };

  const handleEdit = () => {
    // TODO: Implement edit functionality
    console.log('Edit appointment:', appointment.id);
  };

  const handleAddNote = async () => {
    if (!quickNote.trim()) return;
    
    setLoading(true);
    try {
      await onAddNote(appointment.id, quickNote);
      setQuickNote('');
      setShowNote(false);
    } catch (error) {
      console.error('Eroare la adăugarea notiței:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmCancel = async () => {
    setLoading(true);
    try {
      await onCancel(appointment.id, notifyOwner, cancelReason);
      setShowCancel(false);
      onClose();
    } catch (error) {
      console.error('Eroare la anulare:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPatientInitials = (name: string) => {
    return name.split(' ').map(word => word[0]).join('').toUpperCase();
  };

  // Helper functions for patient metadata
  const formatAge = (birthDate: string) => {
    if (!birthDate) return null;
    try {
      const birth = new Date(birthDate);
      const now = new Date();
      const diffInMonths = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
      
      if (diffInMonths < 12) {
        return `${diffInMonths} luni`;
      } else {
        const years = Math.floor(diffInMonths / 12);
        return `${years} ani`;
      }
    } catch {
      return null;
    }
  };

  const maskMicrochip = (microchip: string) => {
    if (!microchip) return null;
    if (microchip.length <= 8) return microchip;
    return `${microchip.slice(0, 4)}...${microchip.slice(-3)}`;
  };

  const formatWeight = (weight: number) => {
    if (!weight || weight <= 0) return null;
    return `${weight} kg`;
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar({ open: true, message: 'Microcip copiat', severity: 'success' });
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { 
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">
              Detalii programare - {appointment.patient.name}
            </Typography>
            <Chip
              icon={statusInfo.icon}
              label={statusInfo.label}
              color={statusInfo.color}
              variant="filled"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {formatDate(appointment.start)} • Durată {Math.round((new Date(appointment.end).getTime() - new Date(appointment.start).getTime()) / 60000)}'
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ 
        overflow: 'auto', 
        flex: 1,
        padding: '20px !important' 
      }}>
        {isPastDue && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Programare trecută de termen
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
          {/* Left Column - Information */}
          <Box sx={{ flex: '0 0 35%', minWidth: 0 }}>
            {/* Patient Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Pacient
              </Typography>
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Avatar 
                  src={appointment.patient.photoUrl} 
                  sx={{ width: 48, height: 48, cursor: 'pointer' }}
                  onClick={() => navigate(`/patients/${appointment.patient.id}`)}
                >
                  {getPatientInitials(appointment.patient.name)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography 
                    variant="h6" 
                    fontWeight="bold" 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                    onClick={() => navigate(`/patients/${appointment.patient.id}`)}
                  >
                    {appointment.patient.name}
                  </Typography>
                  
                  {/* Line 1: species / breed / sex / age */}
                  {(() => {
                    const line1Parts = [
                      appointment.patient.species && appointment.patient.species !== 'necunoscut' ? appointment.patient.species : null,
                      appointment.patient.breed && appointment.patient.breed !== 'necunoscut' ? appointment.patient.breed : null,
                      appointment.patient.sex && appointment.patient.sex !== 'necunoscut' ? appointment.patient.sex : null,
                      formatAge(appointment.patient.birthDate || '')
                    ].filter(Boolean);
                    
                    return line1Parts.length > 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 0.5 }}>
                        {line1Parts.join(' / ')}
                      </Typography>
                    ) : null;
                  })()}
                  
                  {/* Line 2: weight, microchip, sterilized */}
                  {(() => {
                    const line2Parts = [
                      formatWeight(appointment.patient.weight || 0),
                      appointment.patient.microchip ? `Microcip: ${maskMicrochip(appointment.patient.microchip)}` : null,
                      appointment.patient.sterilized ? 'Sterilizat(a)' : null
                    ].filter(Boolean);
                    
                    return line2Parts.length > 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {line2Parts.map((part, index) => {
                          const isMicrochip = part?.includes('Microcip:');
                          return (
                            <span key={index}>
                              {isMicrochip ? (
                                <Tooltip title="Click pentru copiere">
                                  <span 
                                    style={{ 
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                      textDecorationStyle: 'dotted'
                                    }}
                                    onClick={() => copyToClipboard(appointment.patient.microchip || '')}
                                  >
                                    {part}
                                  </span>
                                </Tooltip>
                              ) : (
                                part
                              )}
                              {index < line2Parts.length - 1 && ' • '}
                            </span>
                          );
                        })}
                      </Typography>
                    ) : null;
                  })()}
                  
                  {/* Context badges */}
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {appointment.patient.allergies && appointment.patient.allergies.length > 0 && (
                      <Chip 
                        label="Alergii" 
                        size="small" 
                        color="error" 
                        variant="filled"
                        sx={{ fontSize: '0.7rem', height: '20px' }}
                      />
                    )}
                    {(appointment.patient.aggressive || (appointment.patient.warnings && appointment.patient.warnings.length > 0)) && (
                      <Chip 
                        label="Avertizare" 
                        size="small" 
                        color="warning" 
                        variant="filled"
                        sx={{ fontSize: '0.7rem', height: '20px' }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Owner Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Proprietar
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Box>
                  <Typography variant="h6">
                    {appointment.owner.name}
                  </Typography>
                  <Box display="flex" gap={1} sx={{ mt: 1 }}>
                    {appointment.owner.phone && (
                      <Tooltip title="Sună">
                        <IconButton 
                          size="small" 
                          onClick={() => window.open(`tel:${appointment.owner.phone}`)}
                          sx={{ color: 'pink.main' }}
                        >
                          <PhoneIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Mesaj">
                      <IconButton 
                        size="small"
                        sx={{ color: 'purple.main' }}
                      >
                        <MessageIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Right Column - Context and Actions */}
          <Box sx={{ flex: '0 0 65%', minWidth: 0 }}>
            {/* Visual separator */}
            <Divider sx={{ mb: 3, display: { xs: 'block', md: 'none' } }} />
            
            {/* Appointment Details */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Detalii programare
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                <Typography variant="body2" sx={{ minWidth: '45%' }}>
                  <strong>Serviciu:</strong> {appointment.service}
                </Typography>
                <Typography variant="body2" sx={{ minWidth: '45%' }}>
                  <strong>Medic:</strong> {appointment.doctor.name}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                <Typography variant="body2" sx={{ minWidth: '45%' }}>
                  <strong>Motiv:</strong> {appointment.reason}
                </Typography>
                <Typography variant="body2" sx={{ minWidth: '45%' }}>
                  <strong>Creat la:</strong> {formatDateShort(appointment.createdAt)}
                </Typography>
              </Box>
              {appointment.location && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>Locație / sală:</strong> {appointment.location}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Short History - Only show if there's relevant data */}
            {(appointment.lastVisit || appointment.nextDue) && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                  Istoric scurt
                </Typography>
                {appointment.lastVisit && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      <strong>Ultima vizită:</strong> {formatDateShort(appointment.lastVisit.date)} — {appointment.lastVisit.type}
                    </Typography>
                  </Box>
                )}
                {appointment.nextDue && (
                  <Box>
                    <Typography variant="body2">
                      <strong>Următoare scadențe:</strong> {appointment.nextDue}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Visual separator */}
            <Divider sx={{ my: 3 }} />

            {/* Quick Actions */}
            <Box>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Acțiuni rapide
              </Typography>
              
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} sx={{ '& > *': { minHeight: '36px' } }}>
                {canCheckIn && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCheckIn}
                    disabled={loading}
                    startIcon={<CheckCircleIcon />}
                    size="small"
                  >
                    Check-in
                  </Button>
                )}

                {canOpenConsult && (
                  <Button
                    variant={appointment.status === 'checked_in' ? 'contained' : 'outlined'}
                    color="primary"
                    onClick={handleOpenConsultation}
                    disabled={loading}
                    size="small"
                  >
                    Deschide consultația
                  </Button>
                )}

                {isInConsultation && (
                  <>
                    <Button
                      variant="outlined"
                      color="primary"
                      disabled
                      size="small"
                    >
                      În consultație
                    </Button>
                    <Button
                      variant="text"
                      color="primary"
                      size="small"
                      onClick={() => navigate(`/visit/${appointment.id}`)}
                    >
                      Redeschide
                    </Button>
                  </>
                )}

                {canReschedule && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleReschedule}
                    disabled={loading}
                    size="small"
                  >
                    Reprogramează
                  </Button>
                )}

                {canCancel && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleCancel}
                    disabled={loading}
                    size="small"
                  >
                    Anulează
                  </Button>
                )}

              </Box>

              {/* Quick Note */}
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setShowNote(!showNote)}
                >
                  Adaugă notiță rapidă
                </Button>
                {showNote && (
                  <Box sx={{ mt: 1 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Adaugă o notiță..."
                      value={quickNote}
                      onChange={(e) => setQuickNote(e.target.value)}
                      size="small"
                    />
                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleAddNote}
                        disabled={!quickNote.trim() || loading}
                      >
                        Salvează
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setQuickNote('');
                          setShowNote(false);
                        }}
                      >
                        Anulează
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Reschedule Dialog */}
        {showReschedule && (
          <Box sx={{ mt: 3, p: 2, border: '1px solid #EAEAEA', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Reprogramează programarea
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Funcționalitatea de reprogramare va fi implementată în următoarea versiune.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setShowReschedule(false)}
            >
              Închide
            </Button>
          </Box>
        )}

        {/* Cancel Dialog */}
        {showCancel && (
          <Box sx={{ mt: 3, p: 2, border: '1px solid #EAEAEA', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Anulează programarea
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={notifyOwner}
                  onChange={(e) => setNotifyOwner(e.target.checked)}
                />
              }
              label="Notifică proprietarul"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Motivul anulării (opțional)"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                color="error"
                onClick={handleConfirmCancel}
                disabled={loading}
              >
                Confirmă anularea
              </Button>
              <Button
                variant="outlined"
                onClick={() => setShowCancel(false)}
              >
                Anulează
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        position: 'sticky', 
        bottom: 0, 
        backgroundColor: 'white', 
        borderTop: '1px solid #EAEAEA',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box display="flex" gap={1}>
          <Button 
            onClick={handlePrint} 
            variant="text" 
            color="primary"
            startIcon={<PrintIcon />}
            size="small"
          >
            Imprimă / Export PDF
          </Button>
        </Box>
        <Box display="flex" gap={1}>
          <Button onClick={onClose} variant="outlined" size="small">
            Închide
          </Button>
          {canEdit && (
            <Button onClick={handleEdit} variant="contained" startIcon={<EditIcon />} size="small">
              Editează
            </Button>
          )}
        </Box>
      </DialogActions>

      {/* Snackbar for microchip copy feedback */}
      {snackbar.open && (
        <Box
          role="alert"
          aria-live="polite"
          sx={{ 
            position: 'fixed', 
            bottom: 16, 
            left: 16, 
            bgcolor: snackbar.severity === 'error' ? 'error.main' : snackbar.severity === 'info' ? 'info.main' : 'success.main', 
            color: '#fff', 
            px: 2, 
            py: 1, 
            borderRadius: 1, 
            boxShadow: 3,
            zIndex: 9999
          }}
          onClick={() => setSnackbar(s => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Box>
      )}
    </Dialog>
  );
};

export default AppointmentDetailsModal;
